import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAllInsights } from '@/components/pages/Insights/useInsights';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import useBrains from '@/hooks/useBrains';
import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { useDebtCollectionInsights } from '@/modules/analytics/hooks/useDebtCollectionInsights';
import useInterval from '@/modules/analytics/hooks/useInterval';
import { useSystemInsightsPerformance } from '@/modules/analytics/hooks/useSystemInsightsPerformance';
import { selectBrainId } from '@/redux/session/selectors';
import { BrainType } from '@/shared/types/brain';
import { numberFormatter } from '@/util/util';

import { LocalDateFilter } from './LocalDateFilter';
import { SimpleMetric } from './SimpleMetric';

export const OverviewPerformance = () => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain, getStatus } = useBrains(brainId);

  const isBrainLoading = getStatus === 'pending';
  // If brain is loading, isPublished is true so the metrics branch renders with skeletons
  // instead of briefly flashing the unpublished empty state message.
  // If brain is loaded and version_count > 0, isPublished is true and metrics show real values.
  // If brain is loaded and version_count === 0, isPublished is false and empty state shows.
  const isPublished = isBrainLoading || !!brain?.version_count;
  const isDebtCollection = brain?.brain_type === BrainType.DEBT_COLLECTION;

  // Fetch all insights to check if debt collection insights are registered
  // Only fetch if feature flag is enabled and brain is debt collection type
  const { data: allInsights } = useAllInsights(brainId);

  // Check individually if each debt collection insight is registered
  const { hasPromiseToPayInsight, hasDisputeInsight } = useMemo(() => {
    if (!allInsights || !isDebtCollection) {
      return { hasPromiseToPayInsight: false, hasDisputeInsight: false };
    }
    const insightNames = allInsights.map((insight) => insight.name);
    return {
      hasPromiseToPayInsight: insightNames.includes('promise_to_pay'),
      hasDisputeInsight: insightNames.includes('dispute'),
    };
  }, [allInsights, isDebtCollection]);

  const hasAnyDebtCollectionInsight = hasPromiseToPayInsight || hasDisputeInsight;

  // Date selection persisted to localStorage; range is re-resolved against today on each render.
  const { dateRange, selection, setSelection } = usePersistedDateRange(
    'ai_agent_performance_date_filter'
  );

  // Fetch total conversations using useInterval with local date range
  const totalQuery = useInterval({
    type: 'sessions',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    is_brain: true,
    is_brain_effectiveness: true,
    brainIds: brainId ? [brainId] : undefined,
  });

  // Fetch uncovered conversations for coverage calculation
  const uncoveredQuery = useInterval({
    type: 'sessions',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    is_covered: false,
    is_brain: true,
    is_brain_effectiveness: true,
    brainIds: brainId ? [brainId] : undefined,
  });

  // Fetch system insights performance (AI CSAT and Goal achieved)
  const insightsQuery = useSystemInsightsPerformance({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Fetch debt collection insights (PTP rate and Dispute rate) - only when any insight is registered
  const debtCollectionQuery = useDebtCollectionInsights({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    skip: !hasAnyDebtCollectionInsight,
  });

  const isLoading =
    totalQuery.isLoading ||
    uncoveredQuery.isLoading ||
    insightsQuery.isLoading ||
    (hasAnyDebtCollectionInsight && debtCollectionQuery.isLoading);

  // Calculate total count from interval data (sum of all days)
  const totalCount = useMemo(() => {
    if (!totalQuery.data) {
      return 0;
    }
    return totalQuery.data.reduce((sum, row) => sum + row.counts, 0);
  }, [totalQuery.data]);

  // Calculate uncovered count from interval data (sum of all days)
  const uncoveredCount = useMemo(() => {
    if (!uncoveredQuery.data) {
      return 0;
    }
    return uncoveredQuery.data.reduce((sum, row) => sum + row.counts, 0);
  }, [uncoveredQuery.data]);

  // Calculate formatted and exact counts for total conversations
  const { formattedCount, exactCount } = useMemo(() => {
    return {
      formattedCount: numberFormatter(totalCount),
      exactCount: numberFormatter(totalCount, false),
    };
  }, [totalCount]);

  // Calculate coverage percentage (using Math.ceil like CoverageFieldset does)
  const coveragePercentage = useMemo(() => {
    if (totalCount === 0) {
      return null;
    }
    const coveredCount = totalCount - uncoveredCount;
    return Math.ceil((coveredCount / totalCount) * 100);
  }, [totalCount, uncoveredCount]);

  // Format AI CSAT value (round to 1 decimal place)
  const formattedAiCsat = useMemo(() => {
    if (insightsQuery.avgCsatBrain === null) {
      return null;
    }
    return Math.round(insightsQuery.avgCsatBrain * 10) / 10;
  }, [insightsQuery.avgCsatBrain]);

  // Format Goal achieved percentage (round to nearest integer)
  const formattedGoalAchieved = useMemo(() => {
    if (insightsQuery.goalAchievedPercentage === null) {
      return null;
    }
    return Math.round(insightsQuery.goalAchievedPercentage);
  }, [insightsQuery.goalAchievedPercentage]);

  // Format Promise to Pay rate (round to nearest integer)
  const formattedPromiseToPayRate = useMemo(() => {
    if (debtCollectionQuery.promiseToPayRate === null) {
      return null;
    }
    return Math.round(debtCollectionQuery.promiseToPayRate);
  }, [debtCollectionQuery.promiseToPayRate]);

  // Format Dispute rate (round to nearest integer)
  const formattedDisputeRate = useMemo(() => {
    if (debtCollectionQuery.disputeRate === null) {
      return null;
    }
    return Math.round(debtCollectionQuery.disputeRate);
  }, [debtCollectionQuery.disputeRate]);

  // Use the 4-column grid only when all four metrics render: non-debt brains always show the
  // two system metrics; debt-collection brains need both PTP and dispute insights registered.
  const showFourColumns = !isDebtCollection || (hasPromiseToPayInsight && hasDisputeInsight);

  return (
    <Card className="w-full gap-0 rounded-md border-border bg-background p-0 text-foreground shadow-none">
      <CardHeader className="flex flex-row items-center justify-between p-5">
        <h3 className="text-base font-semibold">{t('ai_agents.overview.performance')}</h3>

        {isPublished && (
          <LocalDateFilter
            selection={selection}
            onSelectionChange={setSelection}
            align="end"
            className="z-40"
          />
        )}
      </CardHeader>

      {isPublished ? (
        <CardContent className="@container px-5 pb-5">
          <div
            className={
              showFourColumns
                ? 'grid grid-cols-2 gap-x-10 gap-y-4 @[600px]:grid-cols-4'
                : 'flex gap-10'
            }
          >
            {hasPromiseToPayInsight && (
              <SimpleMetric
                title={t('ai_agents.overview.promise_to_pay')}
                tooltip={t('ai_agents.overview.promise_to_pay_tooltip')}
                value={formattedPromiseToPayRate !== null ? `${formattedPromiseToPayRate}%` : '–'}
                isLoading={isLoading}
                skeletonWidth="w-12"
              />
            )}
            {hasDisputeInsight && (
              <SimpleMetric
                title={t('ai_agents.overview.dispute_rate')}
                tooltip={t('ai_agents.overview.dispute_rate_tooltip')}
                value={formattedDisputeRate !== null ? `${formattedDisputeRate}%` : '–'}
                isLoading={isLoading}
                skeletonWidth="w-12"
              />
            )}
            {!isDebtCollection && (
              <>
                <SimpleMetric
                  title={`${t('ai_agents.overview.ai_csat')} (1-5)`}
                  tooltip={t('ai_agents.overview.ai_csat_tooltip')}
                  value="–"
                  isLoading={isLoading}
                  skeletonWidth="w-20"
                >
                  {formattedAiCsat !== null ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl leading-none font-semibold tracking-tight text-foreground">
                        {formattedAiCsat}
                      </span>
                      <span className="text-sm leading-6 text-foreground-muted">
                        ({insightsQuery.csatBrainCount}{' '}
                        {t('ai_agents.overview.reviews', { count: insightsQuery.csatBrainCount })})
                      </span>
                    </div>
                  ) : null}
                </SimpleMetric>
                <SimpleMetric
                  title={t('ai_agents.overview.goal_achieved')}
                  tooltip={t('ai_agents.overview.goal_achieved_tooltip')}
                  value={formattedGoalAchieved !== null ? `${formattedGoalAchieved}%` : '–'}
                  isLoading={isLoading}
                  skeletonWidth="w-12"
                />
              </>
            )}
            <SimpleMetric
              title={t('ai_agents.overview.total_conversations')}
              tooltip={t('ai_agents.overview.total_conversations_tooltip')}
              value={formattedCount}
              exactValue={exactCount}
              isLoading={isLoading}
            />
            <SimpleMetric
              title={t('ai_agents.overview.coverage')}
              tooltip={t('analytics.coverage_tooltip')}
              value={coveragePercentage !== null ? `${coveragePercentage}%` : '–'}
              isLoading={isLoading}
              skeletonWidth="w-12"
            />
          </div>
        </CardContent>
      ) : (
        <CardContent className="flex items-center justify-center px-5 pt-5 pb-10">
          <p className="text-sm text-foreground-muted">
            {t('ai_agents.overview.performance_empty')}
          </p>
        </CardContent>
      )}
    </Card>
  );
};
